import { action } from "satcheljs/lib/legacy";
import { InstrumentationService, TeamDataService } from "sh-services";
import { UserSettingsEntity } from "sh-models";
import { getGenericEventPropertiesObject } from "sh-instrumentation";

const MAX_RETRY_ATTEMPTS = 3;

/**
 * Records that a feedback panel was shown by saving the current timestamp.
 * This is used to implement time-based feedback display logic (show first time, then wait N days).
 *
 * @param scenario - Scenario identifier (e.g., "autoScheduleApprove", "autoScheduleReject")
 * @param feedbackKey - Unique identifier for the feedback within the scenario
 */
export let setFeedbackShownTimestamp = action("setFeedbackShownTimestamp")(
    async function setFeedbackShownTimestamp(scenario: string, feedbackKey: string) {
        const userSettings: UserSettingsEntity = await TeamDataService.getUserSettings();
        console.log(`setFeedbackShownTimestamp: Retrieved user settings from backend. feedbackTimestamps:`, JSON.stringify(userSettings.feedbackTimestamps));

        if (!scenario) {
            console.error("setFeedbackShownTimestamp: scenario is required");
            return null;
        }

        if (!feedbackKey) {
            console.error("setFeedbackShownTimestamp: feedbackKey is required");
            return null;
        }

        const timestamp = Date.now();

        // Initialize feedbackTimestamps if it doesn't exist
        if (!userSettings.feedbackTimestamps) {
            console.log(`setFeedbackShownTimestamp: feedbackTimestamps field is missing, initializing empty object`);
            userSettings.feedbackTimestamps = {} as { [scenario: string]: { [feedbackKey: string]: number } };
        }

        // Initialize scenario-specific timestamps if it doesn't exist
        if (!userSettings.feedbackTimestamps[scenario]) {
            console.log(`setFeedbackShownTimestamp: scenario "${scenario}" is missing, initializing empty object`);
            userSettings.feedbackTimestamps[scenario] = {} as { [feedbackKey: string]: number };
        }

        // Set the timestamp for this feedback
        userSettings.feedbackTimestamps[scenario][feedbackKey] = timestamp;

        console.log(`setFeedbackShownTimestamp: Attempting to save feedbackTimestamps for scenario "${scenario}", key "${feedbackKey}" with timestamp ${timestamp}`);
        console.log(`setFeedbackShownTimestamp: Current feedbackTimestamps object:`, JSON.stringify(userSettings.feedbackTimestamps));

        // Try to save to backend with retries
        for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
            try {
                const result = await TeamDataService.saveUserSettings(userSettings);
                console.log(`setFeedbackShownTimestamp: Successfully saved to backend on attempt ${attempt}`);
                console.log(`setFeedbackShownTimestamp: Backend response feedbackTimestamps:`, JSON.stringify(result?.feedbackTimestamps));

                InstrumentationService.logEvent(
                    InstrumentationService.events.SetFeedbackShownTimestamp,
                    [
                        getGenericEventPropertiesObject(InstrumentationService.properties.SmartSchedulingFeedbackScenario, scenario),
                        getGenericEventPropertiesObject(InstrumentationService.properties.SmartSchedulingFeedbackKey, feedbackKey),
                        getGenericEventPropertiesObject(InstrumentationService.properties.SmartSchedulingFeedbackTimestamp, timestamp.toString())
                    ]
                );
                return result;
            } catch (error) {
                console.error(`setFeedbackShownTimestamp: Failed attempt ${attempt}/${MAX_RETRY_ATTEMPTS}`, error);

                InstrumentationService.trackException(
                    error,
                    `setFeedbackShownTimestamp_attempt_${attempt}_scenario_${scenario}_feedbackKey_${feedbackKey}`,
                    "setFeedbackShownTimestamp"
                );

                if (attempt < MAX_RETRY_ATTEMPTS) {
                    // Wait before retrying (exponential backoff)
                    const delayMs = Math.pow(2, attempt) * 100;
                    console.log(`setFeedbackShownTimestamp: Retrying after ${delayMs}ms...`);
                    await new Promise(resolve => setTimeout(() => resolve(undefined), delayMs));
                } else {
                    console.error(`setFeedbackShownTimestamp: All ${MAX_RETRY_ATTEMPTS} attempts failed. Backend may not support feedbackTimestamps field yet.`);
                }
            }
        }
        return null;
    });
